import BulkEditRequestsSelectorCheckbox from './BulkEditRequestsSelectorCheckbox';
import {
  KoobBadge,
  KoobButton,
  KoobCard,
  KoobInputText,
  KoobPlaceholder,
} from '@koob/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useField } from 'formik';
import KoobTitle from '../../../../components/Koob/KoobTitle';
import { useAsync } from 'react-async';
import { getToConnectionRequests } from 'api/node/toConnectionRequest';
import { isEqual } from 'lodash';
import { useApp } from 'hooks';
import { decodeId } from 'utils';

export default function BulkEditOrganizationSelector() {
  const { t } = useTranslation('toConnectionRequest');
  const [
    { value: selectedOrganizationIds },
    ,
    { setValue: setSelectedOrganizationIds },
  ] = useField('organizationIds');
  const { currentOrganizationId, selectedDistributionOrganization } = useApp();
  const dmcOrganizationId =
    selectedDistributionOrganization?.value ?? currentOrganizationId;
  const [search, setSearch] = useState('');

  const filters = {
    dmcId: decodeId(dmcOrganizationId),
  };

  const { data, isLoading } = useAsync({
    promiseFn: getToConnectionRequests,
    filters,
    watchFn: (props, prevProps) => {
      return !isEqual(props.filters, prevProps.filters);
    },
  });

  const request = data?.data || [];
  const formatResult = Object.values(request).reduce((acc, curr) => {
    const id = curr.toOrganization?.id;
    const type = curr.type;
    const index = acc.findIndex((v) => v.toOrganization?.id === id);

    if (index !== -1) {
      acc[index] = {
        ...acc[index],
        [type]: curr,
      };
    } else {
      acc = [...acc, { ...curr, [type]: curr }];
    }
    return acc;
  }, []);

  const connectionRequests = formatResult || [];

  const searchResults = formatResult.filter((request) => {
    return (
      request?.toOrganization?.name
        ?.toLowerCase()
        ?.includes(search.toLowerCase()) ||
      request?.state?.toLowerCase()?.includes(search.toLowerCase()) ||
      request?.type?.toLowerCase()?.includes(search.toLowerCase())
    );
  });

  const selectAll = () => {
    if (search?.length > 0) {
      setSelectedOrganizationIds([
        ...new Set([
          ...selectedOrganizationIds,
          ...searchResults.map((request) => request.toOrganization.id),
        ]),
      ]);
    } else {
      setSelectedOrganizationIds(
        connectionRequests.map((request) => request.toOrganization.id),
      );
    }
  };

  const deselectAll = () => {
    if (search?.length > 0) {
      const searchOrganizationIds = searchResults.map(
        (request) => request.toOrganization.id,
      );
      setSelectedOrganizationIds(
        selectedOrganizationIds.filter(
          (id) => !searchOrganizationIds.includes(id),
        ),
      );
    } else {
      setSelectedOrganizationIds([]);
    }
  };

  return (
    <KoobCard>
      <div className="flex-col space-y-5">
        <div className="flex justify-between items-center">
          <KoobTitle>{t('bulkEdit.requestPromotionSelector.title')}</KoobTitle>
        </div>

        <div className="flex space-x-3">
          <KoobButton
            variant="primary"
            onClick={() => selectAll()}
            size="sm"
            disabled={(search?.length > 0
              ? searchResults
              : connectionRequests
            ).every((r) =>
              selectedOrganizationIds.includes(r.toOrganization.id),
            )}
          >
            {t('bulkEdit.requestPromotionSelector.selectAll')}
          </KoobButton>

          <KoobButton
            variant="primary"
            onClick={() => deselectAll()}
            size="sm"
            disabled={selectedOrganizationIds.length === 0}
          >
            {t('bulkEdit.requestPromotionSelector.deselectAll')}
          </KoobButton>

          <KoobBadge color="gray">
            {t('bulkEdit.requestPromotionSelector.selectedCount', {
              count: selectedOrganizationIds.length,
              total: connectionRequests.length ?? 0,
            })}
          </KoobBadge>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <KoobInputText
            name="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('bulkEdit.requestPromotionSelector.search')}
          />
        </div>

        <div className="h-56 overflow-scroll bg-gray-50 p-5 rounded-md">
          <div className="grid md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-5">
            {!isLoading ? (
              <>
                {searchResults?.map((request) => (
                  <BulkEditRequestsSelectorCheckbox
                    key={request.id}
                    request={request}
                    selected={selectedOrganizationIds.includes(
                      request.toOrganization.id,
                    )}
                    setSelected={(selected) => {
                      const organizationId = request.toOrganization.id;
                      if (selected) {
                        setSelectedOrganizationIds([
                          ...selectedOrganizationIds,
                          organizationId,
                        ]);
                      } else {
                        setSelectedOrganizationIds(
                          selectedOrganizationIds.filter(
                            (id) => id !== organizationId,
                          ),
                        );
                      }
                    }}
                    updating={!!request?.hasActiveJob}
                  />
                ))}
              </>
            ) : (
              <>
                {[...Array(12)].map((_e, index) => (
                  <KoobPlaceholder className="h-10" key={index} />
                ))}
              </>
            )}
          </div>
        </div>
      </div>
    </KoobCard>
  );
}
